export type SupportSettingToggle = {
  enabled: boolean;
};

/**
 * Runtime feature kill switch in svc-support settings.
 * enabled: true → on for everyone. enabled: false → only bypassRequestors; empty list → off for everyone.
 */
export type SupportFeatureSetting = SupportSettingToggle & {
  bypassRequestors: string[];
};

/** svc-zendesk SIERRA_BOOKING_ENQUIRY: bypassRequestors get every reason, enabled only the ticked ones. */
export type BookingEnquirySetting = SupportFeatureSetting & {
  reasons: string[];
  /** Send the other enquiries to Sierra too and post its reply to Slack only. */
  shadow?: boolean;
};

export const BOOKING_ENQUIRY_REASONS = [
  { value: 'inclusions', label: 'Inclusions Information' },
  { value: 'booking_details', label: 'Booking Details Request' },
  { value: 'fine_print', label: 'Fine Print Clarification' },
  { value: 'reassurance', label: 'Reassurance of Confirmed Booking' },
  { value: 'transfers', label: 'Transfer Information' },
] as const;

/** svc-zendesk SIERRA_SPECIAL_REQUEST: LEX sends Flash special requests to the hotel. */
export type SpecialRequestSetting = SupportFeatureSetting & {
  /** Run LEX on the special requests it isn't handling and post its reply to Slack only. */
  shadow?: boolean;
};

export type BypassableFeatureKey =
  | 'SIERRA_EMAIL_AI'
  | 'SALES_INTENT_ROUTING'
  | 'FLIGHT_INTENT_ROUTING'
  | 'SUPPORT_INTENT_CLARIFICATION'
  | 'CUSTOMER_AUTO_SOLVE'
  | 'CUSTOMER_PARTNERSHIP_DEFLECT'
  | 'CUSTOMER_REPLY_MERGE'
  | 'SIERRA_BOOKING_ENQUIRY'
  | 'SIERRA_LUXPLUS_CANCELLATION'
  | 'SIERRA_SPECIAL_REQUEST';

export const BYPASSABLE_FEATURE_KEYS = [
  'SIERRA_EMAIL_AI',
  'SALES_INTENT_ROUTING',
  'FLIGHT_INTENT_ROUTING',
  'SUPPORT_INTENT_CLARIFICATION',
  'CUSTOMER_AUTO_SOLVE',
  'CUSTOMER_PARTNERSHIP_DEFLECT',
  'CUSTOMER_REPLY_MERGE',
  'SIERRA_BOOKING_ENQUIRY',
  'SIERRA_LUXPLUS_CANCELLATION',
  'SIERRA_SPECIAL_REQUEST',
] as const satisfies readonly BypassableFeatureKey[];

export const DEFAULT_BYPASSABLE_FEATURE_SETTING: SupportFeatureSetting = {
  enabled: false,
  bypassRequestors: [],
};

export interface SettingsFormData {
  GENESYS: SupportSettingToggle;
  CHRISTMAS: SupportSettingToggle;
  SIERRA_EMAIL_AI: SupportFeatureSetting;
  SALES_INTENT_ROUTING: SupportFeatureSetting;
  FLIGHT_INTENT_ROUTING: SupportFeatureSetting;
  SUPPORT_INTENT_CLARIFICATION: SupportFeatureSetting;
  CUSTOMER_AUTO_SOLVE: SupportFeatureSetting;
  CUSTOMER_PARTNERSHIP_DEFLECT: SupportFeatureSetting;
  CUSTOMER_REPLY_MERGE: SupportFeatureSetting;
  SIERRA_BOOKING_ENQUIRY: BookingEnquirySetting;
  SIERRA_LUXPLUS_CANCELLATION: SupportFeatureSetting;
  SIERRA_SPECIAL_REQUEST: SpecialRequestSetting;
}

export const DEFAULT_SETTINGS: SettingsFormData = {
  GENESYS: {
    enabled: false,
  },
  CHRISTMAS: {
    enabled: false,
  },
  SIERRA_EMAIL_AI: { ...DEFAULT_BYPASSABLE_FEATURE_SETTING, enabled: true },
  // Keep in sync with svc-zendesk supportSettings fail-closed defaults.
  SALES_INTENT_ROUTING: DEFAULT_BYPASSABLE_FEATURE_SETTING,
  FLIGHT_INTENT_ROUTING: DEFAULT_BYPASSABLE_FEATURE_SETTING,
  SUPPORT_INTENT_CLARIFICATION: DEFAULT_BYPASSABLE_FEATURE_SETTING,
  CUSTOMER_AUTO_SOLVE: DEFAULT_BYPASSABLE_FEATURE_SETTING,
  CUSTOMER_PARTNERSHIP_DEFLECT: DEFAULT_BYPASSABLE_FEATURE_SETTING,
  CUSTOMER_REPLY_MERGE: DEFAULT_BYPASSABLE_FEATURE_SETTING,
  SIERRA_BOOKING_ENQUIRY: { ...DEFAULT_BYPASSABLE_FEATURE_SETTING, reasons: [], shadow: false },
  SIERRA_LUXPLUS_CANCELLATION: DEFAULT_BYPASSABLE_FEATURE_SETTING,
  SIERRA_SPECIAL_REQUEST: { ...DEFAULT_BYPASSABLE_FEATURE_SETTING, shadow: false },
};
